import { liquidGlassSurfaceFillSx } from '@focuslee/shared-mini-app';
import { Box, Modal, Stack } from '@mui/material';
import { Button } from '@telegram-apps/telegram-ui';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useVoice } from '../../contexts/VoiceContext';
import {
  FLOATING_TABBAR_BAR_GAP,
  FLOATING_TABBAR_HORIZONTAL_INSET,
  floatingTabbarBlockHeightPx,
} from '../../helpers/floatingTabbarLayout';
import { VoiceState } from '../../types/voice';
import { LiquidGlassCircleButton } from '../common/LiquidGlassCircleButton';
import { VoiceAudioElement } from './VoiceAudioElement';
import { VoiceModalErrorView } from './VoiceModalErrorView';
import { VoiceModalHeader } from './VoiceModalHeader';
import { VoiceModalListeningView } from './VoiceModalListeningView';
import { VoiceModalProcessingView } from './VoiceModalProcessingView';
import { VoiceModalReviewView } from './VoiceModalReviewView';
import { VoiceModalSuccessView } from './VoiceModalSuccessView';

export const VoiceModal: FC = () => {
  const { t } = useTranslation();
  const {
    voiceState,
    transcript,
    pendingActions,
    voiceError,
    onConfirmAll,
    cancel,
    retryTranscription,
    isRecording,
    recordedAudioUrl,
    recordingSessionId,
  } = useVoice();

  const stateLabelMap: Record<string, string> = {
    [VoiceState.LISTENING]: t('voice.listening'),
    [VoiceState.PROCESSING]: t('voice.processing'),
    [VoiceState.REVIEW]: pendingActions.length > 0 ? t('voice.review', { count: pendingActions.length }) : t('voice.noActions'),
    [VoiceState.SUCCESS]: t('voice.done'),
    [VoiceState.ERROR]: t('voice.loadFailed'),
  };
  const stateLabel = stateLabelMap[voiceState] ?? t('voice.title');

  const [isExiting, setExiting] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayback = useCallback(() => {
    if (!recordedAudioUrl) return;
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      if (el.ended || el.currentTime === 0) setPlaybackProgress(0);
      el.volume = 1;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [recordedAudioUrl]);

  useEffect(() => {
    if (!recordedAudioUrl) {
      setPlaybackProgress(0);
      setIsPlaying(false);
    }
  }, [recordedAudioUrl]);

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isEditingVoiceAction =
    (pathname.includes('/quests/create') ||
      pathname.includes('/quests/postpone') ||
      pathname.includes('/categories/create') ||
      pathname.includes('/achievements/create')) &&
    searchParams.has('voiceActionId');

  const handleClose = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
    setExiting(true);
    setTimeout(() => {
      cancel();
      setExiting(false);
    }, 300);
  }, [cancel]);

  if (voiceState === VoiceState.IDLE && !isExiting) return null;

  const tabbarHeight = floatingTabbarBlockHeightPx;
  const safeAreaBottom =
    'var(--tg-viewport-content-safe-area-inset-bottom, 0px)';
  const safeAreaBottom2 =
    'var(--tg-viewport-safe-area-inset-bottom, 0px)';

  const hasAudioOrTranscript = !!(recordedAudioUrl || transcript);

  return (
    <>
      <Box
        aria-hidden
        onClick={handleClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 'auto',
          height: 'var(--tg-viewport-height, 100vh)',
          zIndex: 1399,
          backgroundColor: 'rgba(0, 0, 0, 0.72)',
          backdropFilter: 'blur(8px)',
          ...(isEditingVoiceAction && { visibility: 'hidden', pointerEvents: 'none' }),
        }}
      />
      <Modal
        open
        onClose={handleClose}
        disablePortal
        hideBackdrop
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 'auto',
          height: 'var(--tg-viewport-height, 100vh)',
          zIndex: 1500,
          pointerEvents: 'none',
          ...(isEditingVoiceAction && { visibility: 'hidden', pointerEvents: 'none' }),
        }}
        slotProps={{ root: { style: { padding: 0, margin: 0 } } }}
      >
          <Box
          sx={{
            position: 'fixed',
            left: `${FLOATING_TABBAR_HORIZONTAL_INSET}px`,
            right: `${FLOATING_TABBAR_HORIZONTAL_INSET}px`,
            bottom: `calc(${tabbarHeight}px + ${FLOATING_TABBAR_BAR_GAP}px + ${safeAreaBottom} + ${safeAreaBottom2})`,
            zIndex: 1,
            pointerEvents: 'auto',
            boxSizing: 'border-box',
            maxHeight: voiceState === VoiceState.LISTENING ? '90vh' : '70vh',
            borderRadius: '20px',
            padding: '20px 16px 16px',
            paddingBottom: '16px',
            transition: 'max-height 0.25s ease-out',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            outline: 'none',
            overflow: 'hidden',
            WebkitOverflowScrolling: 'touch',
            animation: isExiting ? 'voiceSlideDown 0.3s ease-in forwards' : 'voiceSlideUp 0.3s ease-out',
            ...liquidGlassSurfaceFillSx,
          }}
          >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexShrink={0}>
            <VoiceModalHeader voiceState={voiceState} stateLabel={stateLabel} />
            <LiquidGlassCircleButton icon="close" ariaLabel={t('voice.close')} onClick={handleClose} size={32} iconSize={18} />
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: voiceState === VoiceState.LISTENING ? 'visible' : 'auto',
              WebkitOverflowScrolling: 'touch',
              ...(voiceState === VoiceState.LISTENING && {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
              }),
            }}
          >
            {voiceState === VoiceState.LISTENING && isRecording && (
              <VoiceModalListeningView key={recordingSessionId} />
            )}

            {voiceState === VoiceState.PROCESSING && hasAudioOrTranscript && (
              <VoiceModalProcessingView
                playbackProgress={playbackProgress}
                isPlaying={isPlaying}
                onPlayback={handlePlayback}
              />
            )}

            {voiceState === VoiceState.REVIEW && (
              <VoiceModalReviewView
                playbackProgress={playbackProgress}
                isPlaying={isPlaying}
                onPlayback={handlePlayback}
              />
            )}

            {voiceState === VoiceState.ERROR && voiceError && (
              <VoiceModalErrorView
                playbackProgress={playbackProgress}
                isPlaying={isPlaying}
                onPlayback={handlePlayback}
              />
            )}

            {voiceState === VoiceState.SUCCESS && (
              <VoiceModalSuccessView />
            )}
          </Box>

          {recordedAudioUrl && (
            <VoiceAudioElement
              audioRef={audioRef}
              src={recordedAudioUrl}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => {
                setIsPlaying(false);
                setPlaybackProgress(1);
              }}
              onTimeUpdate={(e) => {
                const el = e.currentTarget;
                if (el.duration && isFinite(el.duration))
                  setPlaybackProgress(el.currentTime / el.duration);
              }}
            />
          )}

          {voiceState === VoiceState.REVIEW && (
            <Stack direction="row" gap={1.125} sx={{ flexShrink: 0, pt: 1.5 }}>
              <Button mode="outline" size="m" onClick={handleClose} style={{ flex: 1 }}>
                {t('voice.cancel')}
              </Button>
              <Button
                mode="filled"
                size="m"
                onClick={onConfirmAll}
                disabled={pendingActions.length === 0}
                style={{ flex: 2 }}
              >
                {t('voice.apply', { count: pendingActions.length })}
              </Button>
            </Stack>
          )}

          {voiceState === VoiceState.ERROR && recordedAudioUrl && (
            <Stack direction="row" gap={1.125} sx={{ flexShrink: 0, pt: 1.5 }}>
              <Button mode="outline" size="m" onClick={handleClose} style={{ flex: 1 }}>
                {t('voice.cancel')}
              </Button>
              <Button mode="filled" size="m" onClick={retryTranscription} style={{ flex: 1 }}>
                {t('voice.retry')}
              </Button>
            </Stack>
          )}
          </Box>
      </Modal>
    </>
  );
};
